import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import * as moment from "moment-timezone";
import { Observable } from "rxjs/Observable";
import { catchError, map } from "rxjs/operators";
import { ErrorHandlerService } from "../../shared/services/errorHandler.service";
import { ExecuteService } from "../../shared/services/executeService.service";
import { EditorialMessageContent } from "../../tradingstudio/models/EditorialMessageContent";
import {
  SignalMessage,
  SignalMessagePage,
} from "../../tradingstudio/models/SignalMessage";
import {
  getEventName,
  getPostUrl,
  getSignalValue,
  SignalMessagePageResponse,
  SignalMessageResponse,
} from "../../tradingstudio/models/SignalMessagePageResponse";
import { TradePosition } from "../../tradingstudio/models/TradePosition";
import { SignalBoConfiguration } from "../configuration/SignalBoConfiguration";

@Injectable()
export class SignalBoService {
  private _messagesPageSize: number;

  constructor(
    private _executeService: ExecuteService,
    private _errorHandler: ErrorHandlerService,
    private _signalBoConfiguration: SignalBoConfiguration
  ) {
    this._messagesPageSize = this._signalBoConfiguration.MessagePageSize;
  }

  public GetEditorialMessages(pageNum: number): Observable<SignalMessagePage> {
    const url = `${this._signalBoConfiguration.ApiUrl}${
      this._signalBoConfiguration.MessageEndpoint
    }editorial/${pageNum.toString()}/${this._messagesPageSize}`;
    return this._executeService.ExecuteWithToken("get", url).pipe(
      map((response) => this.mapResponseItem(response)),
      catchError(
        this._errorHandler.handleError<SignalMessagePage>(
          "SignalBoService_GetEditorialMessages"
        )
      )
    );
  }

  public GetEditorialMessageById(id: string): Observable<SignalMessage> {
    const url = `${this._signalBoConfiguration.ApiUrl}${this._signalBoConfiguration.MessageEndpoint}${id}`;
    return this._executeService.ExecuteWithApiToken("get", url).pipe(
      map((response) => this.mapResponseMessage(response)),
      catchError(
        this._errorHandler.handleError<SignalMessage>(
          "SignalBoService_GetEditorialMessages"
        )
      )
    );
  }

  public CreateMessage(
    message: EditorialMessageContent
  ): Observable<SignalMessage> {
    const url = `${this._signalBoConfiguration.ApiUrl}${this._signalBoConfiguration.MessageEndpoint}editorial`;
    return this._executeService
      .ExecuteWithToken("post", url, null, message)
      .pipe(
        map((response) => this.mapResponseMessage(response)),
        catchError(
          this._errorHandler.handleError<SignalMessage>(
            "SignalBoService_CreateMessage"
          )
        )
      );
  }

  public EditMessage(message: SignalMessage): Observable<SignalMessage> {
    const url = `${this._signalBoConfiguration.ApiUrl}${this._signalBoConfiguration.MessageEndpoint}editorial/${message.id}`;
    return this._executeService
      .ExecuteWithToken("put", url, null, message.content)
      .pipe(
        map((response) => this.mapResponseMessage(response)),
        catchError(
          this._errorHandler.handleError<SignalMessage>(
            "SignalBoService_EditMessage"
          )
        )
      );
  }

  public DeleteMessage(id: string): Observable<boolean> {
    const url = `${this._signalBoConfiguration.ApiUrl}${this._signalBoConfiguration.MessageEndpoint}${id}`;
    return this._executeService
      .ExecuteWithToken("delete", url)
      .pipe(
        catchError(
          this._errorHandler.handleError<object>(
            "SignalBoService_DeleteMessage"
          )
        )
      );
  }

  public GetPositions(
    isClosed?: boolean,
    strategies?: string[]
  ): Observable<TradePosition[]> {
    let params = new HttpParams();
    if (isClosed !== null) {
      params = params.set("isClosed", `${isClosed}`);
    }
    if (strategies) {
      strategies.forEach((item) => {
        params = params.append("strategyIds", item);
      });
    }

    return this._executeService
      .ExecuteWithToken(
        "get",
        `${this._signalBoConfiguration.ApiUrl}api/v1/Signal/Filter`,
        params
      )
      .pipe(
        catchError(
          this._errorHandler.handleError<object[]>(
            "SignalsService_GetPositions",
            [],
            true
          )
        )
      );
  }

  private mapResponseItem(
    response: SignalMessagePageResponse
  ): SignalMessagePage {
    const result: SignalMessagePage = {
      pageNumber: response.pageNumber,
      pageSize: response.pageSize,
      total: response.total,
      items: response.items.map(this.mapResponseMessage),
    };

    return result;
  }

  private mapResponseMessage(response: SignalMessageResponse): SignalMessage {
    const result: SignalMessage = {
      id: response.id,
      dateUtc: moment.utc(response.dateUtc),
      content: {
        title: response.content.title,
        link: response.content.link,
        relatedPostContent: response.content.dataRelatedPost,
        relatedWebinarContent: response.content.dataRelatedWebinar,
        relatedRecordingContent: response.content.dataRelatedRecording,
        relatedSignalContent: response.content.dataRelatedSignal,
        imageUrl: response.content.imageUrl,
        description: response.content.description,
        mediaLink: response.content.mediaLink,
        category: response.content.category,
        tags: response.content.tags
          ? response.content.tags.map((x) => {
              return {
                Name: x.name,
                Id: x.id,
              };
            })
          : null,
        market: response.content.market,
        authorId:
          response.content.author != undefined
            ? response.content.author.id
            : undefined,
        authorName:
          response.content.author != undefined
            ? response.content.author.name
            : undefined,
        postLink: response.content ? getPostUrl(response.content) : undefined,
        eventTitle: response.content
          ? getEventName(response.content)
          : undefined,
        signalValue: response.content
          ? getSignalValue(response.content)
          : undefined,
      },
    };

    return result;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/signalbo.service.ts